import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  Paper,
  Typography,
  Box,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Departement, Document_vente, GeneriqueData } from '../../models/models';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../reduxStore/Store';
import { createDocumentVente, setSelectedDocument, setComposantDepot } from '../../reduxStore/documentVenteSlice';
import MyButton from '../MyButton';
import { ButtonType, TypeTiers } from '../../constants/enums';
import { useSnackbar } from '../../components/SnackbarProvider';
import ClientAutoC from '../AutoComplete/ClientAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import ContratAutoC from '../AutoComplete/ContratAutoC';
import SelectDepot from '../Selects/SelectDepot';
import SelectDepartement from '../Selects/SelectDepartement';
import { useDialog } from '../DialogComponent';
import NextDocDispatch from '../../pages/NextDocDispatch';
import { fetchWorkflowTransitions } from '../../reduxStore/WorkflowEtapeSlice';


const DocumentVenteForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedDocument, composantDepot } = useSelector((state: any) => state.DocumentVenteSlice);
  const { showSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);

  const { selectedTypeDoc } = useSelector((state: RootState) => state.SysTypeDocumentSlice);
  const { selectedDepartement } = useSelector((state: RootState) => state.AppSlice);
  const { transitions } = useSelector((state: any) => state.WorkflowEtapeSlice);
  const typeDoc = selectedDocument?.type_document ?? selectedTypeDoc;
  const tiersType: TypeTiers | null = typeDoc?.type_tiers ?? null;
  const tiersObligatoire: boolean = !!typeDoc?.tiers_obligatoire;

  const [formData, setFormData] = useState<Document_vente>({
    id: -1,
    contrat_id: null,
    numero_doc: '',
    date_doc: new Date().toISOString().split('T')[0],
    date_echeance: (() => {
      let d = new Date();
      d.setMonth(d.getMonth() + 1);
      return d.toISOString().split('T')[0];
    })(),
    tiers_id: 0,
    reference: '',
    type_doc_id: selectedTypeDoc ? selectedTypeDoc.id : 0,
    doc_code: selectedTypeDoc?.code ?? '',
    depot_id: 0,
    departement_id: selectedDepartement?.id ?? null,
    tiers: null,
    contrat: null,
    depot: null,
    departement: selectedDepartement ?? null,
  });

  useEffect(() => {
    const sourceCode = (selectedDocument?.type_document ?? selectedTypeDoc)?.code;
    if (sourceCode) dispatch(fetchWorkflowTransitions({ source_code: sourceCode, departement_id: selectedDepartement?.id ?? null }));
  }, [dispatch, selectedDocument?.type_document?.code, selectedTypeDoc?.code, selectedDepartement?.id]);

  useEffect(() => {
    if (selectedDocument) {
      setFormData({
        ...selectedDocument,
        date_doc: new Date(selectedDocument.date_doc).toISOString().split('T')[0],
        date_echeance: new Date(selectedDocument.date_echeance).toISOString().split('T')[0],
      });
    } else if (selectedTypeDoc) {
      setFormData(prev => ({
        ...prev,
        type_doc_id: selectedTypeDoc.id,
        doc_code: selectedTypeDoc.code,
        departement_id: selectedDepartement?.id ?? prev.departement_id,
        departement: selectedDepartement ?? prev.departement,
      }));
    }
  }, [selectedDocument, selectedTypeDoc, selectedDepartement, dispatch]);

  useEffect(() => {
    return () => {
      // Cleanup function to reset form data when component unmounts
      dispatch(setSelectedDocument(null)); // Reset selected document in the store  
    }
  }, [dispatch]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('id') || name === 'type_doc' ? Number(value) : value,
    }));
  };

  const handleTiersChange = (tiers: any | null) => {  // Replace `any` with actual type for Locataire
    setFormData((prev) => ({
      ...prev,
      tiers_id: tiers ? tiers.id : null,
      tiers: tiers, // Ensure it updates the object

    }));
  };
  const handleContratChange = (contrat: any | null) => {  // Replace `any` with actual type for Locataire
    setFormData((prev) => ({
      ...prev,
      contrat_id: contrat ? contrat.id : null,
      contrat: contrat, // Ensure it updates the object
      tiers_id: contrat && contrat.locataire_id ? contrat.locataire_id : null,
      tiers: contrat && contrat.locataire_id ? contrat.locataire : null, // Update tiers based on contrat
    }));
  };



  const handleDepotChange = (depot: GeneriqueData | null) => {
    setFormData((prev) => ({
      ...prev,
      depot_id: depot ? depot.id : 0,
      depot: depot,
    }));
  };

  // Mode "Tous départements" (création uniquement — l'édition préserve toujours le
  // departement_id existant du document) : le département n'a plus de valeur implicite,
  // l'utilisateur doit le choisir explicitement avant de pouvoir sélectionner un dépôt.
  const handleDepartementChange = (dept: Departement | null) => {
    setFormData((prev) => ({
      ...prev,
      departement_id: dept?.id ?? null,
      departement: dept,
      depot_id: 0,
      depot: null,
    }));
  };

  // Second dépôt par défaut (composant en fabrication / source en transfert) — défaut de
  // session (non persisté sur le document) qui pré-remplit le champ de chaque ligne ARTICLE.
  // Modifiable par ligne ensuite dans le LigneDocumentVenteForm.
  const handleComposantDepotChange = (depot: GeneriqueData | null) => {
    dispatch(setComposantDepot(depot));
  };
  const [NextDocDialog, openNextDocDialog] = useDialog({
  modal: true,
  dialogProps: {
    maxWidth: 'lg',
    fullWidth: true
  }
});

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (tiersObligatoire && !formData.tiers_id) {
      showSnackbar('Le tiers est obligatoire pour ce type de document.', 'error');
      return;
    }

    if (!formData.departement_id) {
      showSnackbar('Le département est obligatoire.', 'error');
      return;
    }

    try {
      await dispatch(createDocumentVente(formData)).unwrap();
      showSnackbar('Document enregistré avec succès !', 'success');
    } catch (error) {
      showSnackbar(typeof error === 'string' ? error : `Erreur lors de l'enregistrement`, 'error');
    }
  };

  const tiersAutoc = (tiersType: TypeTiers | null | undefined) => {
    switch (tiersType) {
      case TypeTiers.CLIENT:
        return <ClientAutoC
          onClientChange={handleTiersChange}
          selectedClient={formData.tiers}
          required={tiersObligatoire}
          disabled={!!selectedDocument} />;
      case TypeTiers.FOURNISSEUR:
        return <FournisseurAutoC
          onFournisseurChange={handleTiersChange}
          selectedFournisseur={formData.tiers}
          required={tiersObligatoire}
          disabled={!!selectedDocument} />;
      default:
        return null;
    }
  };
 const getNextDocAction = () => {
  if (!transitions || transitions.length === 0) return null;
  const first = transitions[0];
  const label = transitions.length > 1
    ? `Créer document suivant (${transitions.length} options)`
    : `Créer ${first.next_doc_libelle ?? first.next_doc_code}`;
  return { label };
};

const nextDocAction = getNextDocAction();

const handleCreateNextDoc = () => {
  if (!nextDocAction) return;
  openNextDocDialog();
};
  return (
    <Paper elevation={1} sx={{ padding: 1.5, marginBottom: 1.5 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {selectedDocument ? `Modifier ${selectedDocument.type_document.libelle}` : `Ajouter ${selectedTypeDoc?.libelle}`}
        </Typography>
        <IconButton size="small" onClick={() => setExpanded(prev => !prev)}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
      <form onSubmit={handleSubmit}>
        {/* Ligne 1 : identifiants du document */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Numéro Document"
              name="numero_doc"
              fullWidth
              value={formData.numero_doc || (formData.id === -1 ? 'Auto-généré' : '')}
              size="small"
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Date Document"
              name="date_doc"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.date_doc}
              onChange={handleInputChange}
              size="small"
              disabled={!!selectedDocument}
              // Inventaire : jamais daté dans le futur (règle vérifiée aussi par l'API)
              inputProps={typeDoc?.est_inventaire ? { max: new Date().toISOString().split('T')[0] } : undefined}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Date Échéance"
              name="date_echeance"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.date_echeance}
              onChange={handleInputChange}
              size="small"
              disabled={!!selectedDocument}
            />
          </Grid>


          {!selectedDocument && !selectedDepartement && (
            <Grid item xs={12} sm={2}>
              <SelectDepartement
                onDepartementChange={handleDepartementChange}
                selectedDepartement={formData.departement}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={2}>
            <SelectDepot
              label={typeDoc?.est_transfert ? 'Dépôt destination' : 'Depot'}
              onDepotChange={handleDepotChange}
              selectedDepot={formData.depot}
              departement_id={formData.departement_id}
              disabled={!!selectedDocument}
            />
          </Grid>

          {(!!typeDoc?.est_fabrication || !!typeDoc?.est_transfert) && (
            <Grid item xs={12} sm={2}>
              <SelectDepot
                label={typeDoc?.est_transfert ? 'Dépôt source' : 'Dépôt composant'}
                onDepotChange={handleComposantDepotChange}
                selectedDepot={composantDepot}
                departement_id={formData.departement_id}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={3}>
            <ContratAutoC
              onContratChange={handleContratChange}
              selectedContrat={formData.contrat}
              disabled={!!selectedDocument}
            />
          </Grid>
        </Grid>

        {/* Ligne 2 : tiers + référence */}
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            {tiersAutoc(tiersType)}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Référence"
              name="reference"
              type="text"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.reference}
              onChange={handleInputChange}
              size="small"
            />
          </Grid>
        </Grid>

        {/* Bouton submit caché : active la soumission via Enter sur n'importe quel champ */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" />

        <Box mt={1} display="flex" justifyContent="space-between">
          <div>
            <MyButton
              actionType={ButtonType.INSERT}
              variant="contained"
              onClick={handleSubmit}
            >
              {selectedDocument ? "Modifier" : "Ajouter"}
            </MyButton>
          </div>

          {nextDocAction && selectedDocument && (
            <MyButton
              variant="outlined"
              onClick={handleCreateNextDoc}
            >
              {nextDocAction.label}
            </MyButton>
          )}
        </Box>

      </form>
      </Collapse>
      <NextDocDialog>
        <NextDocDispatch/>
      </NextDocDialog>
    </Paper>
  );
};

export default DocumentVenteForm;
